import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, ExternalLink, Pencil, Save } from "lucide-react";

const STATUSES = ["Saved", "Applied", "Interview Scheduled", "Offer Received", "Rejected", "Follow-up Required"];

export default function ApplicationTracker() {
  const [apps, setApps] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newApp, setNewApp] = useState({
    title: "", company: "", platform: "", location: "", link: "",
    status: "Saved", applied_date: "", resume_version: "", notes: "",
  });

  const load = async () => {
    try {
      const r = await api.get("/applications");
      setApps(r.data || []);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newApp.title || !newApp.company) {
      toast.error("Title and company required");
      return;
    }
    try {
      await api.post("/applications", newApp);
      setNewApp({ title: "", company: "", platform: "", location: "", link: "", status: "Saved", applied_date: "", resume_version: "", notes: "" });
      setCreating(false);
      toast.success("Added");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/applications/${id}`, { status });
      setApps(apps.map((a) => (a.application_id === id ? { ...a, status } : a)));
    } catch {
      toast.error("Could not update");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/applications/${id}`);
      setApps(apps.filter((a) => a.application_id !== id));
      toast.success("Removed");
    } catch {}
  };

  const grouped = STATUSES.map((s) => ({ status: s, items: apps.filter((a) => a.status === s) }));

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#1E6FE6]">Application tracker</p>
          <h1 className="font-display font-black text-4xl tracking-tight mt-1">Your pipeline</h1>
          <p className="text-slate-400 mt-2 max-w-xl">Track everything you apply for, in one Kanban board.</p>
        </div>
        <button
          data-testid="app-new"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-[#1E6FE6] hover:bg-[#1257C4] text-white px-5 py-2.5 text-sm font-bold shadow-lg shadow-indigo-500/20"
        >
          <Plus size={16} /> Add application
        </button>
      </div>

      {creating && (
        <div className="glass p-6 fade-up-delay-1 space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-display font-bold text-xl">New application</div>
            <button onClick={() => setCreating(false)} className="text-slate-400 hover:text-[#FF6DAE]"><X size={18} /></button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Inp label="Job title" value={newApp.title} onChange={(v) => setNewApp({ ...newApp, title: v })} testid="napp-title" />
            <Inp label="Company" value={newApp.company} onChange={(v) => setNewApp({ ...newApp, company: v })} testid="napp-company" />
            <Inp label="Platform" value={newApp.platform} onChange={(v) => setNewApp({ ...newApp, platform: v })} />
            <Inp label="Location" value={newApp.location} onChange={(v) => setNewApp({ ...newApp, location: v })} />
            <Inp label="Link" value={newApp.link} onChange={(v) => setNewApp({ ...newApp, link: v })} />
            <Inp label="Applied date" value={newApp.applied_date} onChange={(v) => setNewApp({ ...newApp, applied_date: v })} placeholder="YYYY-MM-DD" />
            <Inp label="Resume version" value={newApp.resume_version} onChange={(v) => setNewApp({ ...newApp, resume_version: v })} />
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Status</span>
              <select
                value={newApp.status}
                onChange={(e) => setNewApp({ ...newApp, status: e.target.value })}
                className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3 py-2.5 text-sm bg-white"
              >
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <div className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Notes</span>
            <textarea
              value={newApp.notes}
              onChange={(e) => setNewApp({ ...newApp, notes: e.target.value })}
              rows={3}
              className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setCreating(false)} className="rounded-xl bg-white border-2 border-white/10 px-4 py-2 text-sm font-semibold">Cancel</button>
            <button data-testid="napp-save" onClick={create} className="inline-flex items-center gap-2 rounded-xl bg-[#1E6FE6] text-white px-5 py-2 text-sm font-bold">
              <Save size={14} /> Save
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-4 overflow-x-auto pb-4 fade-up-delay-2">
        {grouped.map((g) => (
          <div key={g.status} className="min-w-[280px] sm:min-w-[300px] flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-sm">{g.status}</div>
              <span className="text-xs text-slate-400 bg-white/[0.06] rounded-full px-2 py-0.5 font-semibold">{g.items.length}</span>
            </div>
            <div className="space-y-3 min-h-[100px]">
              {g.items.map((a) => (
                <Card key={a.application_id} app={a} onStatus={updateStatus} onDelete={del} />
              ))}
              {!g.items.length && (
                <div className="text-xs text-slate-400 border-2 border-dashed border-white/10 rounded-2xl p-6 text-center">
                  No items
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Card({ app, onStatus, onDelete }) {
  return (
    <div className="glass p-4 hover-lift" data-testid={`app-card-${app.application_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display font-bold text-sm truncate">{app.title}</div>
          <div className="text-xs text-slate-400 truncate">{app.company}{app.location ? " · " + app.location : ""}</div>
        </div>
        <button onClick={() => onDelete(app.application_id)} className="text-slate-300 hover:text-red-500 shrink-0">
          <X size={14} />
        </button>
      </div>
      {app.platform && <div className="mt-2 text-[10px] uppercase tracking-wider text-[#1E6FE6] font-bold">{app.platform}</div>}
      {app.notes && <div className="mt-2 text-xs text-slate-300 line-clamp-2">{app.notes}</div>}
      <div className="mt-3 flex items-center gap-2">
        <select
          value={app.status}
          onChange={(e) => onStatus(app.application_id, e.target.value)}
          className="flex-1 rounded-lg border border-white/10 text-xs px-2 py-1.5 bg-white"
        >
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        {app.link && (
          <a href={app.link} target="_blank" rel="noopener noreferrer" className="grid place-items-center w-7 h-7 rounded-lg text-slate-400 hover:bg-white/[0.06]">
            <ExternalLink size={12} />
          </a>
        )}
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm"
      />
    </label>
  );
}
